import React, { useEffect, useState } from "react"
import styled from "styled-components"
import BarChart from "../components/Charts/BarChart"
import MonitoringTable from "../components/MonitoringTable"
import axios from "axios"
import dayjs from "dayjs"
import { CO2_FACTOR } from "../util/Factor"
import StackedBarChartForMonitoring from "../components/StackedBarChartForMonitoring"
import CardFrame5 from "../components/CardFrame5"
import CardIllust2 from "../components/CardIllust2"
import CardFrame5Renewable from "../components/CardFrame5/CardFrame5Renewable"
import MonitoringTable2 from "../components/MonitoringTable2"

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex: 1 0 0;
  align-self: stretch;
`

const Grid1 = styled.div`
  display: flex;
  width: 1392px;
  align-items: center;
  gap: 20px;
`

const CardFrame1 = styled.div`
  display: flex;
  height: 226px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  border-radius: 16px;
  border: 1px solid var(--base-gray40, #bfbfbf);
  background: var(--base-white100, #fff);
`

const GraphWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`

const TitleLegend = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  color: var(--base-gray90, #404040);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const Grid2 = styled.div`
  display: flex;
  width: 1392px;
  align-items: flex-start;
  gap: 20px;
`

const Group = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 20px;
  flex: 1 0 0;
  flex-wrap: wrap;
`

const CardFrame2 = styled.div`
  display: flex;
  width: 455px;
  height: 260px;
  padding: 16px;
  justify-content: center;
  align-items: center;

  border-radius: 16px;
  border: 1px solid var(--base-gray40, #bfbfbf);
  background: var(--base-white100, #fff);
`

const Graph3 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const CardFrame6 = styled.div`
  display: flex;
  height: 296px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  border-radius: 16px;
  border: 1px solid var(--base-gray40, #bfbfbf);
  background: var(--base-white100, #fff);
`

const Table2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  flex: 1 0 0;
  align-self: stretch;
`

const FilterPlusButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`

const Filter = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const SelectWrapper = styled.div`
  display: flex;
  width: 104px;
  padding: 6px 8px;
  justify-content: space-between;
  align-items: center;

  border-radius: 8px;
  border: 1px solid var(--base-gray30, #d1d1d1);
  background: var(--base-white60, rgba(255, 255, 255, 0.6));
`

const StyledSelect = styled.select`
  color: var(--base-gray100, #262626);
  font-family: SUIT;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  border: transparent;
  background-color: transparent;

  &:focus {
    outline: none;
  }
`

const StyledOption = styled.option`
  color: #333;
  background-color: white;
  padding: 10px;
`

interface SmartFarm {
  time: Date
  type: string // 예: "발전(Wh)", "사용(Wh)"
  farm: string // 예: "4", "6", "정읍"
  value: number
}

interface JeongeupTodayData {
  fourFarmData: SmartFarm[]
  sixFarmData: SmartFarm[]
  sumData: SmartFarm[]
}

const RenewableEnergyMornitoring = () => {
  const [location, setLocation] = useState<string>("정읍")
  const [year, setYear] = useState<string>("2025")
  const [type, setType] = useState<string>("전력(Wh)")
  const [site, setSite] = useState<string>("전체")
  const [data, setData] = useState<JeongeupTodayData>({
    fourFarmData: [],
    sixFarmData: [],
    sumData: [],
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/smart-farm/today`
        )

        setData(response.data)
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
      }
    }

    fetchData()
  }, [])

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFarm = e.target.value
    setLocation(newFarm)
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFarm = e.target.value
    setYear(newFarm)
  }

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFarm = e.target.value
    setType(newFarm)
  }

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFarm = e.target.value
    setSite(newFarm)
  }

  return (
    <Wrapper>
      <Grid1>
        <CardIllust2
          title="재생 E 발전량 통계"
          value={location}
          setValue={handleSelectChange}
        />
        <CardFrame1>
          <GraphWrapper>
            <TitleLegend>
              실시간 재생 E 발전현황
              <LegendItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <circle
                    cx="7"
                    cy="7"
                    r="5"
                    stroke="#FCB251"
                    stroke-width="4"
                  />
                </svg>
                태양광 발전량
              </LegendItem>
            </TitleLegend>
            {data?.sumData && (
              <BarChart
                width="900px"
                height="200px"
                color="#FCB251"
                chartData={data?.sumData
                  ?.filter((v) => v.type === "발전(Wh)") // 조건에 맞는 데이터만 필터링
                  .map((v) => ({
                    date: dayjs(v.time).format("HH시"),
                    value: v.value,
                  }))}
              />
            )}
          </GraphWrapper>
        </CardFrame1>
      </Grid1>
      <Grid2>
        <Group>
          <CardFrame2>
            <Graph3>
              <TitleLegend>
                동별 재생 E 발전현황
                <Legend>
                  <LegendItem>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="#689CF6"
                        stroke-width="4"
                      />
                    </svg>
                    4동
                  </LegendItem>
                  <LegendItem>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="#AC8CFF"
                        stroke-width="4"
                      />
                    </svg>
                    6동
                  </LegendItem>
                </Legend>
              </TitleLegend>

              <StackedBarChartForMonitoring
                width="100%"
                height="90%"
                color={["#689CF6", "#AC8CFF"]}
                data={[...data?.fourFarmData, ...data.sixFarmData]
                  ?.filter((v) => v.type === "발전(Wh)") // 조건에 맞는 데이터만 필터링
                  .map((v) => ({
                    name: v.farm,
                    date: dayjs(v.time).format("HH"),
                    value: Number(v.value),
                  }))}
              />
            </Graph3>
          </CardFrame2>
          <CardFrame2>
            <Graph3>
              <TitleLegend>
                동별 탄소 저감량
                <Legend>
                  <LegendItem>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="#689CF6"
                        stroke-width="4"
                      />
                    </svg>
                    4동
                  </LegendItem>
                  <LegendItem>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="#AC8CFF"
                        stroke-width="4"
                      />
                    </svg>
                    6동
                  </LegendItem>
                </Legend>
              </TitleLegend>

              <StackedBarChartForMonitoring
                width="100%"
                height="90%"
                color={["#689CF6", "#AC8CFF"]}
                data={[...data?.fourFarmData, ...data.sixFarmData]
                  ?.filter((v) => v.type === "발전(Wh)") // 조건에 맞는 데이터만 필터링
                  .map((v) => ({
                    name: v.farm,
                    date: dayjs(v.time).format("HH"),
                    value: (Number(v.value) / 1000) * CO2_FACTOR,
                  }))}
              />
            </Graph3>
          </CardFrame2>
          <CardFrame6>
            <Table2>
              <FilterPlusButton>
                <Filter>
                  <FilterItem>
                    조회년도
                    <SelectWrapper>
                      <StyledSelect value={year} onChange={handleYearChange}>
                        <StyledOption value="2024">
                          2024&nbsp;&nbsp;&nbsp;
                          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </StyledOption>
                        <StyledOption value="2025">2025</StyledOption>
                      </StyledSelect>
                    </SelectWrapper>
                  </FilterItem>
                  <FilterItem>
                    에너지원
                    <SelectWrapper>
                      <StyledSelect value={type} onChange={handleTypeChange}>
                        <StyledOption value="전력">
                          전력 [Wh]&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </StyledOption>
                        <StyledOption value="?"></StyledOption>
                      </StyledSelect>
                    </SelectWrapper>
                  </FilterItem>
                  <FilterItem>
                    동
                    <SelectWrapper>
                      <StyledSelect value={site} onChange={handleSiteChange}>
                        <StyledOption value="전체">
                          전체&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </StyledOption>
                        <StyledOption value="4동">4동</StyledOption>
                        <StyledOption value="6동">6동</StyledOption>
                      </StyledSelect>
                    </SelectWrapper>
                  </FilterItem>
                </Filter>
              </FilterPlusButton>
              <MonitoringTable2 site={site} year={year} />
            </Table2>
          </CardFrame6>
        </Group>
        <CardFrame5Renewable />
      </Grid2>
    </Wrapper>
  )
}

export default RenewableEnergyMornitoring
